import { VALIDATION } from '../types';
import type { DayEntry, CompanySettings } from '../types';

export interface ValidationError {
  field: string;
  message: string;
}

export function validateHours(hours: number): ValidationError | null {
  if (typeof hours !== 'number' || !Number.isFinite(hours)) {
    return { field: 'hours', message: 'Bitte eine gültige Zahl eingeben' };
  }
  if (hours < VALIDATION.hours.min) {
    return { field: 'hours', message: 'Stunden dürfen nicht negativ sein' };
  }
  if (hours > VALIDATION.hours.max) {
    return { field: 'hours', message: `Maximal ${VALIDATION.hours.max} Stunden erlaubt` };
  }
  return null;
}

export function validateNote(note: string | undefined): ValidationError | null {
  if (note && note.length > VALIDATION.noteMaxLength) {
    return {
      field: 'note',
      message: `Maximal ${VALIDATION.noteMaxLength} Zeichen erlaubt`,
    };
  }
  return null;
}

export function validateDayEntry(entry: Partial<DayEntry>): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!entry.date || !/^\d{4}-\d{2}-\d{2}$/.test(entry.date)) {
    errors.push({ field: 'date', message: 'Ungültiges Datumsformat' });
  }

  if (entry.hours !== undefined) {
    const hoursError = validateHours(entry.hours);
    if (hoursError) errors.push(hoursError);
  }

  if (entry.note !== undefined) {
    const noteError = validateNote(entry.note);
    if (noteError) errors.push(noteError);
  }

  return errors;
}

export function validateCompanySettings(
  settings: Partial<CompanySettings>
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (settings.vacationDaysFullTime !== undefined) {
    if (
      settings.vacationDaysFullTime < VALIDATION.vacationDays.min ||
      settings.vacationDaysFullTime > VALIDATION.vacationDays.max
    ) {
      errors.push({
        field: 'vacationDaysFullTime',
        message: `Urlaubstage müssen zwischen ${VALIDATION.vacationDays.min} und ${VALIDATION.vacationDays.max} liegen`,
      });
    }
  }

  if (settings.hoursPerDayFullTime !== undefined) {
    if (
      settings.hoursPerDayFullTime < VALIDATION.hoursPerDay.min ||
      settings.hoursPerDayFullTime > VALIDATION.hoursPerDay.max
    ) {
      errors.push({
        field: 'hoursPerDayFullTime',
        message: `Sollstunden müssen zwischen ${VALIDATION.hoursPerDay.min} und ${VALIDATION.hoursPerDay.max} liegen`,
      });
    }
  }

  if (settings.hourlyWage !== undefined) {
    if (
      settings.hourlyWage < VALIDATION.hourlyWage.min ||
      settings.hourlyWage > VALIDATION.hourlyWage.max
    ) {
      errors.push({
        field: 'hourlyWage',
        message: `Stundenlohn muss zwischen ${VALIDATION.hourlyWage.min} und ${VALIDATION.hourlyWage.max} € liegen`,
      });
    }
  }

  return errors;
}

export function parseGermanNumber(value: string): number {
  // Ersetzt deutsches Komma durch Punkt
  const normalized = value.replace(',', '.');
  const parsed = parseFloat(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function formatGermanNumber(value: number | null | undefined, decimals: number = 2): string {
  return (value ?? 0).toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}
